"use client";
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import OrgService, { OrgListItem } from "@/services/org.service";
import SystemService, { SystemListItem } from "@/services/system.service";
import { debugError } from "@/lib/debug";
import { useAuth } from "@/context/AuthContext";

export type Organization = {
  id: string;
  name: string;
  category?: string | null;
  status?: string;
  activeSystems?: number;
};

export type System = {
  id: string;
  name: string;
  organizationId: string;
  status?: string;
  deviceCount?: number;
  sensorCount?: number;
};

export type LlmProvider = "local" | "private" | "openai" | "anthropic" | "gemini" | "copilot";

export const LLM_PROVIDER_OPTIONS: { value: LlmProvider; label: string }[] = [
  { value: "local", label: "Edge (llama.cpp)" },
  { value: "private", label: "Private (vLLM)" },
  { value: "openai", label: "ChatGPT (OpenAI)" },
  { value: "anthropic", label: "Claude (Anthropic)" },
  { value: "gemini", label: "Gemini (Google)" },
  { value: "copilot", label: "Copilot (Microsoft)" },
];

// Persists the last org/system/device/sensor selection across reloads - a
// full browser reload previously always fell back to org[0]/system[0]/no
// device/no sensor since nothing was ever saved. Org/system are restored
// directly here (both lists are already fetched at this layer); device/sensor
// are only IDs until something fetches the device/sensor lists for the
// restored system, so they're exposed as "pending" hints for the header
// selector (src/components/header/OrgSystemSelector.tsx) to apply once it
// loads those lists and can confirm the IDs still exist.
const STORAGE_KEY = "sensowatch:selection";

type StoredSelection = {
  orgId?: string | null;
  systemId?: string | null;
  deviceId?: string | null;
  sensorIds?: string[] | null;
};

function readStoredSelection(): StoredSelection {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredSelection(selection: StoredSelection) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Private browsing / storage disabled / quota exceeded - selection
    // simply won't survive a reload, which is the pre-existing behavior.
  }
}

function normalizeSensorIds(selectedSensor: any | null): string[] {
  if (!selectedSensor) return [];
  const list = Array.isArray(selectedSensor) ? selectedSensor : [selectedSensor];
  return list.map((s) => s?.id || s?.sensor_name || s?.name).filter(Boolean);
}

type AppContextType = {
  organizations: Organization[];
  systems: System[];
  selectedOrg: Organization | null;
  selectedSystem: System | null;
  selectedDevice: any | null;
  selectedSensor: any | null;
  selectedLlmProvider: LlmProvider;
  setSelectedOrg: (org: Organization | null, system?: System | null, device?: any | null, sensors?: any | null) => void;
  setSelectedSystem: (system: System | null) => void;
  setSelectedDevice: (device: any | null) => void;
  setSelectedSensor: (sensor: any | null) => void;
  setSelectedLlmProvider: (provider: LlmProvider) => void;
  getSystemsForOrg: (orgId: string) => System[];
  refreshSystemsForOrg: (orgId: string) => Promise<System[]>;
  refreshOrganizations: () => Promise<Organization[]>;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  // Device/sensor IDs restored from localStorage, still awaiting
  // confirmation against a freshly-fetched device/sensor list. Consume once
  // (whether or not the IDs turn out to still be valid) via
  // clearPendingDeviceSensor so a later manual change isn't overwritten.
  pendingDeviceId: string | null;
  pendingSensorIds: string[];
  clearPendingDeviceSensor: () => void;
};


const AppContext = createContext<AppContextType | undefined>(undefined);

const mapOrgToContextOrganization = (org: OrgListItem): Organization => ({
  id: org.org_id,
  name: org.name,
  category: org.category,
  status: org.status,
  activeSystems: org.active_systems,
});

const mapSystemToContextSystem = (system: SystemListItem, orgId: string): System => ({
  id: system.system_id,
  name: system.name,
  organizationId: orgId,
  status: system.status,
  deviceCount: system.device_count,
  sensorCount: system.sensor_count,
});

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading: authLoading } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<System | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<any | null>(null);
  const [selectedSensor, setSelectedSensor] = useState<any | null>(null);
  const [selectedLlmProvider, setSelectedLlmProvider] = useState<LlmProvider>("gemini");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Read once at construction time, not in an effect - loadData (below)
  // needs this synchronously on the very first run to restore org/system.
  const storedSelectionRef = useRef<StoredSelection>(readStoredSelection());
  const [pendingDeviceId, setPendingDeviceId] = useState<string | null>(
    storedSelectionRef.current.deviceId || null
  );
  const [pendingSensorIds, setPendingSensorIds] = useState<string[]>(
    storedSelectionRef.current.sensorIds || []
  );
  const clearPendingDeviceSensor = useCallback(() => {
    setPendingDeviceId(null);
    setPendingSensorIds([]);
  }, []);

  // Fetch systems for a specific org
  const fetchSystemsForOrg = useCallback(async (orgId: string): Promise<System[]> => {
    try {
      const systems = await SystemService.listSystems(orgId);
      return systems.map((system) => mapSystemToContextSystem(system, orgId));
    } catch (err) {
      debugError('Error fetching systems:', err);
      return [];
    }
  }, []);

  // Load initial data
  const loadData = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setSystems([]);
      setSelectedOrg(null);
      setSelectedSystem(null);
      setSelectedDevice(null);
      setSelectedSensor(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch organizations
      const orgs = (await OrgService.listOrgs()).map(mapOrgToContextOrganization);
      setOrganizations(orgs);

      if (orgs.length > 0) {
        const stored = storedSelectionRef.current;
        const restoredOrg = stored.orgId ? orgs.find((o) => o.id === stored.orgId) : undefined;
        const orgToSelect = restoredOrg || orgs[0];
        setSelectedOrg(orgToSelect);

        const orgSystems = await fetchSystemsForOrg(orgToSelect.id);
        setSystems(orgSystems);

        if (orgSystems.length > 0) {
          const restoredSystem = restoredOrg && stored.systemId
            ? orgSystems.find((s) => s.id === stored.systemId)
            : undefined;
          setSelectedSystem(restoredSystem || orgSystems[0]);
          // Only worth attempting device/sensor restoration if org+system
          // both matched what was saved - otherwise the saved device/sensor
          // almost certainly belong to a different system.
          if (!restoredSystem) {
            clearPendingDeviceSensor();
          }
        } else {
          clearPendingDeviceSensor();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      debugError('Error loading app data:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchSystemsForOrg, clearPendingDeviceSensor]);

  // Initial load
  useEffect(() => {
    if (authLoading) return;
    loadData();
  }, [authLoading, loadData]);

  // Persist the current selection on every change so a reload can restore
  // it. Deliberately does not persist selectedLlmProvider - that's a
  // per-session preference, not part of "what am I looking at".
  useEffect(() => {
    writeStoredSelection({
      orgId: selectedOrg?.id || null,
      systemId: selectedSystem?.id || null,
      deviceId: selectedDevice?.id || selectedDevice?.device_name || null,
      sensorIds: normalizeSensorIds(selectedSensor),
    });
  }, [selectedOrg, selectedSystem, selectedDevice, selectedSensor]);

  // Get systems for a specific org (from current systems state)
  const getSystemsForOrg = useCallback(
    (orgId: string) => {
      return systems.filter((s) => s.organizationId === orgId);
    },
    [systems]
  );

  // `systems`/`organizations` are only fetched once per org selection - the
  // Dashboard's Systems widget and the header selector both read straight
  // from this context, so creating/deleting a system or org elsewhere
  // (CreateSystem.tsx, DeleteSystem.tsx, etc., which only refresh their own
  // page-local list) left those two stale until a full reload. Exposed so
  // any create/delete flow can also refresh the shared state it actually
  // affects.
  const refreshSystemsForOrg = useCallback(
    async (orgId: string) => {
      const orgSystems = await fetchSystemsForOrg(orgId);
      if (selectedOrg?.id === orgId) {
        setSystems(orgSystems);
      }
      return orgSystems;
    },
    [fetchSystemsForOrg, selectedOrg?.id]
  );

  const refreshOrganizations = useCallback(async () => {
    const orgs = (await OrgService.listOrgs()).map(mapOrgToContextOrganization);
    setOrganizations(orgs);
    return orgs;
  }, []);

  // Handle org selection - update selected org and fetch its systems. Takes
  // optional device/sensors so a caller that's changing org+system+device+
  // sensor all in one action (the header selector's multi-step modal) can
  // pass the final desired device/sensor here instead of setting them
  // separately right after - this function's own async tail always used to
  // unconditionally null them out, which would otherwise silently wipe out
  // a device/sensor selection made in the same "Confirm" action.
  const handleSetSelectedOrg = useCallback(async (
    org: Organization | null,
    system?: System | null,
    device?: any | null,
    sensors?: any | null
  ) => {
    setSelectedOrg(org);
    clearPendingDeviceSensor();
    if (org) {
      setLoading(true);
      const orgSystems = await fetchSystemsForOrg(org.id);
      setSystems(orgSystems);
      // If a system is provided, use it, otherwise auto-select first system
      if (system) {
        setSelectedSystem(system);
      } else {
        setSelectedSystem(orgSystems[0] || null);
      }
      setSelectedDevice(device ?? null);
      setSelectedSensor(sensors ?? null);
      setLoading(false);
    } else {
      setSelectedSystem(null);
      setSelectedDevice(null);
      setSelectedSensor(null);
    }
  }, [fetchSystemsForOrg, clearPendingDeviceSensor]);

  // Switching system used to leave a stale device/sensor from the *previous*
  // system silently selected (only switching org cleared them) - pages could
  // end up rendering data scoped to a device that doesn't belong to the
  // system currently shown elsewhere in the UI. Clear both here too.
  const handleSetSelectedSystem = useCallback((system: System | null) => {
    setSelectedSystem(system);
    setSelectedDevice(null);
    setSelectedSensor(null);
    clearPendingDeviceSensor();
  }, [clearPendingDeviceSensor]);

  return (
    <AppContext.Provider
      value={{
        organizations,
        systems,
        selectedOrg,
        selectedSystem,
        selectedDevice,
        selectedSensor,
        selectedLlmProvider,
        setSelectedOrg: handleSetSelectedOrg,
        setSelectedSystem: handleSetSelectedSystem,
        setSelectedDevice,
        setSelectedSensor,
        setSelectedLlmProvider,
        getSystemsForOrg,
        refreshSystemsForOrg,
        refreshOrganizations,
        loading,
        error,
        refreshData: loadData,
        pendingDeviceId,
        pendingSensorIds,
        clearPendingDeviceSensor,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
